import { Suspense, lazy, useMemo } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import Layout from './pages/Layout'
import Loader from './components/Loader'

import * as Sentry from "@sentry/react"
import FallbackUI from './components/FallbackUI'
import NewLoader from './components/NewLoader'
import NewSuspenseLoader from './components/NewSuspenseLoader'

import LandingPage from './pages/LandingPage'
import LongAudioProvider from './components/LongAudioProvider'
import { mainQuizSubjects } from './utilities/constants'
import { useSelector } from 'react-redux'
import { selectLandingPageDetails } from './redux/otherDetailsSlice'

const OnboardingPage = lazy(() => import(/* webpackPrefetch: true */'./pages/OnboardingPage'))
export const preloadOnboardingPage = () => import('./pages/OnboardingPage')

const NewMainQuiz = lazy(() => import(/* webpackPrefetch: true */'./pages/NewMainQuiz'))
export const preLoadNewMainQuiz = () => import('./pages/NewMainQuiz')

const MCQPage = lazy(() => import(/* webpackPrefetch: true */'./pages/MCQPage'))
export const preLoadMCQPage = () => import('./pages/MCQPage')

const PowerMinutePage = lazy(() => import(/* webpackPrefetch: true */ './pages/PowerMinutePage'))
export const preloadPowerMinutePage = () => import(/* webpackPrefetch: true */ './pages/PowerMinutePage')

const Register = lazy(() => import(/* webpackPrefetch: true */ './pages/Register'))

const NewAnalysisPage = lazy(() => import(/* webpackPrefetch: true */'./pages/NewAnalysisPage'))
export const preLoadAnalysisPage = () => import('./pages/NewAnalysisPage')

const BundlePage = lazy(() => import(/* webpackPrefetch: true */ './pages/BundlePage'))
export const preloadBundlePage = () => import('./pages/BundlePage')

const AddOnPage = lazy(() => import(/* webpackPrefetch: true */ './pages/AddOnPage'))
export const preloadAddOnPage = () => import(/* webpackPrefetch: true */ './pages/AddOnPage')

const CourseAndSkillSelectionPage = lazy(()=> import(/* webpackPrefetch: true */ './pages/SkillSelectionPage'))
export const preloadCourseAndSkillSelectionPage = ()=> import('./pages/SkillSelectionPage')

const ShareCertificate = lazy(() => import('./pages/ShareCertificate'))

const SkillPage = lazy(() => import('./pages/SkillPage'))
const PostCertifcatePurchase = lazy(() => import(/* webpackPrefetch: true */ './pages/PostCertifcatePurchase'))
const SuggestedBundle = lazy(() => import(/* webpackPrefetch: true */ './pages/SuggestedBundle'))
const NewPaidPostPaymentThankYou = lazy(() => import(/* webpackPrefetch: true */ './pages/PostCertifcatePurchase/NewPaidPostPaymentThankYou'))

const NewRedeemPage = lazy(() => import('./pages/NewRedeemPage'))
const UnPaidUsers = lazy(() => import('./pages/UnPaidUsers'))

const CertifiedPricing = lazy(() => import('./pages/CertifiedPricingPage'))
const MultiskillSelection2 = lazy(() => import('./pages/MultiskillSelectionVersion2'))
const PricingPageVersion2 = lazy(() => import('./pages/PricingPage/PricingPageVersion2'))
const SkillLevelPage = lazy(() => import('./pages/PersonaliseSkillPage'))
const PersonaliseAnalysis = lazy(() => import('./pages/PersonaliseAnalysisPage'))
const PersonaliseQuestionarySection = lazy(() => import('./pages/PersonaliseQuestionsPage'))
const PersonalisedCourse = lazy(() => import('./pages/PersonaliseCoursePage'))
const CertificateCourse = lazy(() => import('./pages/CertifiedCoursePage'))
const CourseLoader = lazy(() => import('./components/CourseLoader'))
const PersonalisedPricing = lazy(() => import('./pages/PersonalisedPricingPage'))
const SharePage = lazy(()=> import('./pages/SharePage'))
const ThankYou = lazy(() => import('./pages/AnalysisPage/ThankYou'))
const PageNotFound = lazy(() => import('./components/PageNotFound'))

function App() {

    const { pathname } =useLocation()

    const landingPageDetails = useSelector(selectLandingPageDetails)
    const skillName = landingPageDetails?.skill_name

    const showMainQuiz = useMemo(() => {
        if(!skillName){
            return false
        }
        return mainQuizSubjects?.includes(skillName?.toLowerCase())
    },[skillName])

    return (
        <Sentry.ErrorBoundary fallback={({ resetError }) => <FallbackUI resetError={resetError}/>}>
            {showMainQuiz && <LongAudioProvider />}
            <Routes>
                <Route path='/:skill_name?/:utm_source?' element={<LandingPage/>}/>
                <Route path='/' element={<Layout/>}>
                    <Route path='1/course-page' element={<Suspense fallback={<Loader/>}><CourseAndSkillSelectionPage/></Suspense>}/>
                    <Route path='2/skill-page' element={<Suspense fallback={<Loader/>}><SkillPage/></Suspense>}/>
                    <Route path='analysis'>
                        <Route path='certificate-bundle' element={<Suspense fallback={<Loader />}><BundlePage /></Suspense>} />
                        <Route path='course-bundle' element={<Suspense fallback={<Loader />}><AddOnPage /></Suspense>}/>
                        <Route path='leaderboard'>
                            <Route path='v2' element={<Suspense fallback={<Loader/>}><PostCertifcatePurchase/></Suspense>}/>
                            <Route path='un-paid' element={<Suspense fallback={<Loader/>}><PostCertifcatePurchase/></Suspense>}/>
                            <Route path='bundle-page' element={<Suspense fallback={<Loader/>}><SuggestedBundle /></Suspense>}/>
                        </Route>
                        <Route path='skillcertificates/v2' element={<Suspense fallback={<Loader/>}><MultiskillSelection2/></Suspense>}/>
                        <Route path='thank-you' element={<Suspense fallback={<Loader/>}><NewPaidPostPaymentThankYou /></Suspense>}/>
                    </Route>
                    <Route path='personalised-course'>
                        <Route index element={<Suspense fallback={<Loader/>}><SkillLevelPage/></Suspense>} />
                        <Route path='questions/:id' element={<Suspense fallback={<Loader/>}><PersonaliseQuestionarySection/></Suspense>} />
                        <Route path='loader' element={<Suspense fallback={<Loader/>}><CourseLoader/></Suspense>} />
                        <Route path='certificate-course' element={<Suspense fallback={<Loader/>}><CertificateCourse/></Suspense>} />
                        <Route path='analysis' element={<Suspense fallback={<Loader/>}><PersonaliseAnalysis/></Suspense>} />
                        <Route path='pricing' element={<Suspense fallback={<Loader/>}><CertifiedPricing/></Suspense>} />
                        <Route path='personalised-pricing' element={<Suspense fallback={<Loader/>}><PersonalisedPricing/></Suspense>} />
                        <Route path='course' element={<Suspense fallback={<Loader/>}><PersonalisedCourse/></Suspense>} />
                    </Route>
                    <Route path='pricing'>
                        <Route index element={<Suspense fallback={<Loader/>}><PricingPageVersion2/></Suspense>}/>
                        <Route path='thank-you' element={<Suspense fallback={<Loader/>}><ThankYou/></Suspense>}/>
                    </Route>
                    <Route path='/error' element={<PageNotFound/>}/>
                    <Route path='*' element={<PageNotFound/>}/>
                </Route>
                <Route path='mcq/:id' element={<Suspense fallback={<NewLoader />}>{showMainQuiz ? <NewMainQuiz key={pathname}/> : <MCQPage key={pathname} />}</Suspense>}/>
                <Route path='powerminute' element={<Suspense fallback={<NewLoader />}><PowerMinutePage key={pathname} /></Suspense>}/>
                <Route path='/onboarding' element={<Suspense fallback={<NewLoader />}><OnboardingPage/></Suspense>}/>
                <Route path='register' element={<Suspense fallback={<NewSuspenseLoader trackEvent={false}/>}><Register /></Suspense>}/>
                <Route path='/analysis'>
                    <Route path='leaderboard/share' element={<Suspense fallback={<NewLoader/>}><SharePage/></Suspense>}/>
                    <Route path=':token?' element={<Suspense fallback={<NewLoader />}><NewAnalysisPage /></Suspense>} />
                    <Route path='leaderboard/paid' element={<Suspense fallback={<NewLoader />}><NewRedeemPage /></Suspense>} />
                    <Route path='leaderboard/share-certificate' element={<Suspense fallback={<NewLoader />}><ShareCertificate /></Suspense>} />
                    <Route path='un-paid' element={<Suspense fallback={<NewLoader />}><UnPaidUsers /></Suspense>} />
                </Route>
            </Routes>
        </Sentry.ErrorBoundary>
    )
}

export default App;
